import { FC, MouseEvent, useState, useMemo, ReactNode, useCallback } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'

import MainLayout from 'containers/main-layout/main-layout'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import NewslineCard from 'components/cards/newsline-card/newsline-card'
import NewWebinarCard from '../../cards/new-webinar-card/new-webinar-card'
import CourseCardInWebinars from 'components/cards/course-card-in-webinars/course-card-in-webinars'
const NoFutureSubscription = dynamic(
  () => import('./no-webinar-subscription/no-webinar-subscription')
)
const NoTargetWebinars = dynamic(() => import('./no-webinar-target/no-webinar-target'))
const FiltersPC = dynamic(() => import('../../filters/filter-pc/filter-pc'))
const FiltersMobile = dynamic(() => import('../../filters/filter-mobile/filter-mobile'))
const FilterCards = dynamic(() => import('../../filters/filter-cards/filter-cards'))
const SearchPagination = dynamic(() => import('../../search/search-pagination/search-pagination'))
import ProjectCard from 'components/cards/project-card/project-card'
import Auth from 'store/auth'
import useWebinarSubscription from 'hooks/useWebinarSubscription'
import useGetNmoCourses from 'hooks/useGetNmoCourses'
import useSyncURL from '../useSyncURL'
import useCommonSearch from '../useCommonSearch'
import usePCWindow from 'hooks/usePCWindow'

import { originFilter } from 'utils/analytics/other/filter_usage/filter_usage'
import { setElementArrActive } from 'utils/formatFutureWebinars'
import { getCardsMaterials, createUrlForBtn, getQueryWords } from './utils'
import { cutHashFromURL } from 'utils/getURLData'
import { INewslineCard, IWebinarCard, IProjectCard } from 'constants/types/card.types'
import { IOptions } from 'constants/types/common.types'
import { Key, CARDS_ON_PAGE } from 'constants/search-data/search-data'
import { A_LOG_REG_HASH, originRegWebinars } from 'utils/analytics/other/registration/registration'

import {
  generateConfig,
  InitValues,
  Option,
  OptionAliases,
} from 'constants/search-data/webinar-search-data'
import { MaterialTypes, Pathname } from 'constants/data'
import { popupName } from 'constants/auth/auth-data'
import { ScreenSize } from 'constants/media-constanst'
import { originSubscribe, originFWebinar } from 'utils/analytics/webinars/webinar_future'
import { originPWebinar } from 'utils/analytics/webinars/webinar_past'
import useBannerTelegramBottom from 'store/popups/useBannerTelegramBottom'

interface IWebinarSearch {
  lectures: IOptions[]
  themes: IOptions[]
  specialities: IOptions[]
  selectedOption: string
  children: ReactNode
  isBigPC: boolean
  setSelectedOption: (option: string) => void
  targetInitParams?: IOptions
  initCards?: {
    count: number
    rows: INewslineCard[] | IWebinarCard[]
  }
}

const WebinarSearch: FC<IWebinarSearch> = ({
  lectures,
  themes,
  specialities,
  selectedOption,
  children,
  isBigPC,
  setSelectedOption,
  targetInitParams,
  initCards,
}: IWebinarSearch) => {
  const router = useRouter()
  const { isAuthorized, isSubscribedToTelegram } = Auth.useAuth()
  const { unsubscribeWebinar, subscribeWebinar } = useWebinarSubscription()

  const [isInitState, setInitState] = useState(true)
  const isTarget = Boolean(router.query.speciality)
  const isMobile = usePCWindow(ScreenSize.mobile)

  const coursesCards = useGetNmoCourses()
  const { openBannerTelegramBottom, isBannerTelegramBottomWasOpened } = useBannerTelegramBottom()

  const config = useMemo(
    () => generateConfig(lectures, themes, specialities, isTarget),
    [themes, lectures, specialities, isTarget, isAuthorized]
  )

  const path = cutHashFromURL(router.asPath)

  const selectOptions = {
    [Key.LECTURS]: lectures,
    [Key.THEMES]: themes,
    [Key.SPECIALITIES]: isTarget ? undefined : specialities,
  }

  const formatedInitData = useMemo(
    () => ({
      ...InitValues,
      cards: {
        ...InitValues.cards,
        [selectedOption]: initCards?.rows || [],
      },
      allCount: {
        ...InitValues.allCount,
        [selectedOption]: initCards?.count || 0,
      },
    }),
    []
  )

  const {
    data,
    isLoading,
    handleCards,
    getCards,
    handleParameterSet,
    handleReset,
    handlePageChange,
  } = useCommonSearch({
    InitValues: formatedInitData,
    selectedOption,
    getCardsMaterials,
    setInitState,
    selectOptions,
    isInitState,
    isMobile,
  })

  if (isTarget && targetInitParams) {
    data.params[selectedOption][Key.SPECIALITIES] = [targetInitParams]
  }

  const handleCardData = (id: number, isChecked: boolean | undefined) => {
    return async (e: MouseEvent<HTMLElement>) => {
      e.preventDefault()

      if (isAuthorized) {
        try {
          if (isChecked) {
            await unsubscribeWebinar(id)

            const newWebinarState = setElementArrActive(data.cards[Option.wantSee], id)
            handleCards(newWebinarState, Option.wantSee)
          } else {
            !isSubscribedToTelegram &&
              !isBannerTelegramBottomWasOpened &&
              openBannerTelegramBottom()
            await subscribeWebinar(id)
            const newWebinarState = setElementArrActive(data.cards[Option.future], id)
            handleCards(newWebinarState, Option.future)
          }

          if (isChecked && selectedOption === Option.wantSee) {
            setTimeout(() => getCards(selectedOption, true), 1500)
          }

          const newWebinarState = setElementArrActive(
            data.cards[selectedOption],
            id,
            'isSubscribed',
            !isChecked
          )
          handleCards(newWebinarState)
        } catch {
          console.log('Не удается отправить запрос')
        }
      } else {
        router.push(`${path}#${popupName.registration}`, undefined, { shallow: true })
        sessionStorage.setItem(A_LOG_REG_HASH, originRegWebinars.SUBWEBINARS)
      }
    }
  }

  let pushCourseCounter = 0
  const insertCourse = (card: any, index: number) => {
    if (selectedOption === Option.videoProjects) return null
    const isCourseCardRender =
      index !== 0 && // Не первая
      (index + 1) % 3 === 0 && // После каждого третьего вебинара
      pushCourseCounter <= coursesCards?.length - 1 // Если курсы закончились

    if (isCourseCardRender) {
      pushCourseCounter++
      return card
    }
  }

  const renderedCards = useMemo(() => {
    return (data.cards[selectedOption] as INewslineCard[] | IWebinarCard[] | IProjectCard[])?.map(
      (card, index) => {
        const cardProps = {
          ...card,
          selectedThemes: data.params[selectedOption].selectedThemes,
          selectedLecturs: data.params[selectedOption].lectures,
        }
        const createCard = () => {
          switch (selectedOption) {
            case Option.past:
              return (
                <NewslineCard
                  {...(cardProps as INewslineCard)}
                  materialType={MaterialTypes.WEBINAR}
                  isLoaded
                  cutQueryWords={getQueryWords(data.params[selectedOption].inputText as string)}
                  AData={{ origin: originPWebinar.WEBINAR_PAST_LIST }}
                />
              )
            case Option.videoProjects:
              return <ProjectCard {...(card as IProjectCard)} />
            default:
              return (
                <NewWebinarCard
                  {...(cardProps as IWebinarCard)}
                  isLoaded
                  isFullInfo
                  cutQueryWords={getQueryWords(data.params[selectedOption].inputText as string)}
                  handleToggle={handleCardData(card?.id, card?.isSubscribed)}
                  AData={{
                    originSub:
                      selectedOption !== Option.wantSee
                        ? originSubscribe.WEBINAR_FUTURE_LIST
                        : originSubscribe.NOORIGIN,
                    originClick:
                      selectedOption !== Option.wantSee
                        ? originFWebinar.WEBINAR_FUTURE_LIST
                        : originFWebinar.WEBINAR_WANTSEE,
                  }}
                />
              )
          }
        }
        const webinarCard = createCard()

        const courseCard = coursesCards?.[pushCourseCounter]
        const urlCard =
          selectedOption === Option.videoProjects
            ? `/${Pathname.VIDEOPROJECTS}/${card.id}`
            : `/${Pathname.WEBINAR}/${card.linkTranslation || card.id}`

        return {
          url: urlCard,
          courseNmo: insertCourse(
            <CourseCardInWebinars
              {...courseCard}
              urlBtn={createUrlForBtn(courseCard?.linkTranslation)}
              isAbTest
            />,
            index
          ),
          item: webinarCard,
        }
      }
    )
  }, [data.cards[selectedOption], selectedOption, isBigPC, isAuthorized])

  const goToAllWebinars = useCallback(() => {
    const option = OptionAliases.find((o) => o.option === selectedOption)?.alias
    router.push({ pathname: `/${Pathname.WEBINAR}`, query: { type: option } }, undefined, {
      shallow: true,
    })
    data.params[selectedOption][Key.SPECIALITIES] = []
  }, [selectedOption])

  useSyncURL(selectedOption, OptionAliases, data.params, true)

  const filterPCProps = {
    parameters: data.params[selectedOption],
    handleParameterSet,
    handleReset,
    config: config[selectedOption],
    isBigPC,
  }

  const filterMobileProps = {
    parameters: data.params[selectedOption],
    handleParameterSet,
    handleReset,
    config: config[selectedOption],
    allCount: data.allCount[selectedOption],
  }

  const FilterCardsProps = {
    cards: renderedCards,
    handlePageChange,
    isLoading,
    component:
      selectedOption === Option.wantSee ? (
        <NoFutureSubscription setSelectedOption={setSelectedOption} />
      ) : isTarget ? (
        <NoTargetWebinars goToAllWebinars={goToAllWebinars} selectedOption={selectedOption} />
      ) : null,
  }

  return (
    <MainLayout isScreenHeight>
      <WidthLimiter noLeftPadding noRightPadding fullHeight>
        <div>
          {children}
          <div>
            {selectedOption !== Option.videoProjects && (
              <FiltersPC origin={originFilter.WEBINAR_LIST} {...filterPCProps} />
            )}
            <FilterCards {...FilterCardsProps} oneColumn={selectedOption !== Option.past} />
          </div>
          {!isLoading && (
            <SearchPagination
              maxPage={Math.ceil(data.allCount[selectedOption] / CARDS_ON_PAGE) || 0}
              currentPage={Number(data.params[selectedOption].page)}
              handleParameterSet={handleParameterSet}
            />
          )}
        </div>
      </WidthLimiter>
      {!isBigPC && selectedOption !== Option.videoProjects && (
        <FiltersMobile origin={originFilter.WEBINAR_LIST} {...filterMobileProps} />
      )}
    </MainLayout>
  )
}
export default WebinarSearch
